import { Icon, Input, Switch } from "@elaniintc/goes-design-system";
import { useMemo } from "react";

import type { NodeVariable, Variable } from "../../types";
import {
  getPlaceholder,
  getRestrictionFunction,
} from "../../if/comparativeValues";
import { cn } from "@/utils/theme";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import type { ComponentProps } from "react";
import type { ValueInputType } from "../types";
import ComparativeValueInputCalendar from "../../if/ComparativeValueInput.calendar";
import { VariableSelector } from "./VariableSelector";

/**
 * Maps ValueInputType to an array of compatible variable types for filtering
 * Used for VariableSelector variable filtering - includes alternative type names
 */
const getCompatibleVariableTypes = (
  valueInputType: ValueInputType,
): string[] => {
  switch (valueInputType) {
    case "string":
    case "regex":
      return ["string", "str", "text"];
    case "number":
    case "positive-integer":
      return ["number", "num", "integer", "float", "int"];
    case "date":
      return ["date", "datetime"];
    case "boolean":
      return ["boolean", "bool"];
    case "variable":
      return []; // No filter for generic variable type - show all
    default:
      return []; // No filter - show all
  }
};

export interface TypeValueInputProps {
  valueType: ValueInputType;
  inputType: "input" | "variable";
  value: string | Variable;
  onValueChange: (value: string | Variable) => void;
  inputVariables: NodeVariable[];
  placeholder?: string;
  showInputIcon?: boolean;
  filterByType?: string; // Filtrar variables por tipo específico (legacy)
  filterByTypes?: string[]; // Filtrar variables por múltiples tipos compatibles
  group?:
    | "middle"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down"
    | "middle-left"
    | "middle-right";
}

const getIconName = (columnType: string): string => {
  switch (columnType) {
    case "string":
      return "text";
    case "number":
      return "hashtag";
    case "datetime":
      return "calendar";
    case "boolean":
      return "toggle-large-on";
    case "variable":
      return "table";
    default:
      return "text";
  }
};

const getMaxLength = (valueType: ValueInputType) => {
  switch (valueType) {
    case "string":
      return 255;
    case "number":
    case "positive-integer":
      return 15;
    default:
      return undefined;
  }
};

export const TypeValueInput = ({
  onValueChange,
  inputVariables,
  group,
  inputType,
  value,
  valueType,
  placeholder,
  showInputIcon = true,
  filterByType,
  filterByTypes,
}: TypeValueInputProps) => {
  // Get compatible types for the current valueType (used when no explicit filter is provided)
  // Memoize to avoid recalculating on every render
  const compatibleTypes = useMemo(() => {
    return filterByTypes ?? getCompatibleVariableTypes(valueType);
  }, [filterByTypes, valueType]);

  if (inputType !== "variable" && valueType === "date") {
    // Adjust date to compensate for timezone offset
    const adjustedDate = (() => {
      if (!value || typeof value !== "string") return value as string;

      try {
        // If date is in ISO format, add local time to avoid UTC conversion
        const dateStr = value as string;
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // YYYY-MM-DD format - add local time to avoid UTC conversion
          return `${dateStr}T12:00:00`;
        }
        return dateStr;
      } catch {
        return value as string;
      }
    })();

    return (
      <ComparativeValueInputCalendar
        group={group}
        defaultDate={adjustedDate}
        onChangeDate={onValueChange}
      />
    );
  }

  if (inputType !== "variable" && valueType === "boolean") {
    return (
      <div
        className={cn(
          "px-3 py-2 flex items-center w-full border-neutral leading-3xl text-body-loud bg-neutral-object-accent-silent",
          group === "right"
            ? "border-r border-y rounded-r-lg"
            : "border-r border-b rounded-ee-lg",
        )}
      >
        <Switch
          id={"switch-id"}
          checked={value === "true"}
          onCheckedChange={(checked) => {
            onValueChange(checked ? "true" : "false");
          }}
        />
        <span className="text-body font-normal ml-2">
          {value === "true" ? "Verdadero" : "Falso"}
        </span>
      </div>
    );
  }

  if (inputType !== "variable" && valueType !== "variable") {
    return (
      <div className="relative w-full">
        <Input
          group={group}
          className={cn("w-full", showInputIcon && "pl-9")}
          placeholder={placeholder ?? getPlaceholder(valueType)}
          value={value as string}
          maxLength={getMaxLength(valueType)}
          onChange={(e) => {
            const func = getRestrictionFunction(valueType || "");
            onValueChange(func(e.target.value));
          }}
        />
        {showInputIcon && (
          <Icon
            type="far"
            name={getIconName(valueType)}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-body"
          />
        )}
      </div>
    );
  }

  if (inputType !== "variable" && valueType === "variable") {
    return (
      <DropdownList
        group={group}
        widthClassName="flex-1"
        heightClassName="h-[210px]"
        placeholder={placeholder ?? "Elige una variable"}
        options={inputVariables.flatMap((n) =>
          (n.variables || []).map((variable) => ({
            value: variable.id,
            label: variable.name,
            detail: variable.type,
          })),
        )}
        selectedValue={{
          value: (value as Variable).id,
          label: (value as Variable).name,
          icon: (value as Variable).data as ComponentProps<typeof Icon>,
        }}
        selectedGroup={{
          groupId: "group",
          groupLabels: ["variables"],
          items: [],
          icon: {
            type: "far",
            name: "table",
            size: "xs",
            className: "text-info-quiet",
            variant: "info",
          },
        }}
        toggleSelection={(value) => {
          onValueChange({
            id: value.value,
            name: value.label,
            type: value.detail as Variable["type"],
            data: value.icon as object,
          } as Variable);
        }}
      />
    );
  }

  if (inputType === "variable") {
    return (
      <VariableSelector
        placeholder={placeholder}
        variables={inputVariables}
        selectedVariable={value as Variable}
        onSelectVariable={(variable) => onValueChange(variable || "")}
        triggerGroup={group}
        filterByType={filterByType}
        filterByTypes={
          compatibleTypes && compatibleTypes.length > 0
            ? compatibleTypes
            : undefined
        }
      />
    );
  }
};
